import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { getMyBranches } from "../services/auth";
import { clearRequestBranchIds, setRequestBranchIds } from "./branchRequestContext";

const BranchContext = createContext();

export const BranchProvider = ({ children }) => {
	const isAuthenticated = useIsAuthenticated();
	const { instance, inProgress, accounts } = useMsal();
	const activeAccount = instance.getActiveAccount() || accounts?.[0];

	const accountKey = activeAccount?.homeAccountId ?? "";

	const [branches, setBranches] = useState([]);
	const [loading, setLoading] = useState(false);
	const [selectedBranchId, setSelectedBranchId] = useState(null);

	const fetchedForRef = useRef("");

	useEffect(() => {
		if (!isAuthenticated || !accountKey || inProgress !== InteractionStatus.None) {
			clearRequestBranchIds();
			return;
		}

		if (fetchedForRef.current === accountKey) return;

		const controller = new AbortController();
		setLoading(true);

		getMyBranches(controller.signal)
			.then((list) => {
				fetchedForRef.current = accountKey;
				setBranches(Array.isArray(list) ? list : []);
			})
			.catch((error) => {
				// AbortController cancel is expected on remounts – ignore silently.
				const isCanceled =
					error?.name === "CanceledError" ||
					error?.code === "ERR_CANCELED" ||
					String(error?.message || "").toLowerCase().includes("canceled");

				if (!isCanceled) {
					setBranches([]);
				}
			})
			.finally(() => {
				setLoading(false);
			});

		return () => controller.abort();
	}, [isAuthenticated, accountKey, inProgress]);


	const normalizedBranches = useMemo(() => {
		return branches
			.map((b) => {
				if (!b) return null;
				if (typeof b === "string" || typeof b === "number") {
					return { id: b, name: String(b) };
				}
				if (typeof b === "object") {
					const id =
						b.id ??
						b.Id ??
						b.branchId ??
						b.BranchId ??
						b.terminalId ??
						b.TerminalId;
					const name =
						b.name ??
						b.Name ??
						b.branchName ??
						b.BranchName ??
						b.terminalName ??
						b.TerminalName ??
						String(id ?? "");
					if (id == null) return null;
					return { ...b, id, name };
				}
				return null;
			})
			.filter(Boolean);
	}, [branches]);

	useEffect(() => {
		if (selectedBranchId != null) return;
		if (normalizedBranches.length === 0) return;
		setSelectedBranchId(normalizedBranches[0].id);
	}, [normalizedBranches, selectedBranchId]);

	useEffect(() => {
		if (normalizedBranches.length === 0) {
			clearRequestBranchIds();
			return;
		}

		const allIds = normalizedBranches
			.map((b) => (b?.id != null ? String(b.id).trim() : ""))
			.filter((id) => id.length > 0);

		if (allIds.length === 0) {
			clearRequestBranchIds();
			return;
		}

		setRequestBranchIds(allIds);
	}, [normalizedBranches]);

	return (
		<BranchContext.Provider value={{ branches: normalizedBranches, loading, selectedBranchId, setSelectedBranchId }}>
			{children}
		</BranchContext.Provider>
	);
};

export const useBranch = () => {
	return useContext(BranchContext);
};